import { subYears } from 'date-fns'
import { List } from 'immutable'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { usePrevious } from 'react-use'

import { addAlertSignature } from '../../../actions/alerts'
import { fetchTimeRegistrations } from '../../../api/time-registrations'
import { CostCenterAccounting } from '../../../model/accountingIntegration'
import Company from '../../../model/company'
import CostCenter from '../../../model/costCenter'
import Department from '../../../model/department'
import Employee from '../../../model/employee'
import LeaveType from '../../../model/leaveType'
import Project from '../../../model/project'
import SalaryType from '../../../model/salaryType'
import TimeRegistration, {
  TimeRegistrationClass,
  TimeRegistrationCreationFields,
  TimeRegistrationMutableFields,
} from '../../../model/timeRegistration'
import { DateFormat } from '../../../model/types'
import { TimeRegistrationReducer } from '../../../reducers/timeRegistrations'
import { formatAPIDate, formatDate, getDate } from '../../../utils/date-utils'
import { formatLeaveTypeName, formatTimeRegistrationClass } from '../../../utils/format-utils'
import { formatSavingText } from '../../../utils/loading-utils'
import { formatCurrency, formatDisplayNumber } from '../../../utils/number-utils'
import { getSalaryType } from '../../../utils/salary-type-utils'
import { t } from '../../../utils/translation-utils'
import Button from '../../elements/button'
import Col from '../../elements/grid/col'
import Row from '../../elements/grid/row'
import Icon from '../../elements/icon'
import Modal from '../../elements/modal'
import Table from '../../elements/table'
import Tooltip from '../../elements/tooltip'
import { TimeRegistrationMode } from '../../types'
import DumbLink from '../../widgets/DumbLink'
import LoadingOverlay from '../../widgets/LoadingOverlay'
import DetailedTimeRegistrationEdit from './DetailedTimeRegistrationEdit'

type Props = {
  company: Company
  employee: Employee
  mode: TimeRegistrationMode
  canEditObjects: boolean
  canApproveObjects: boolean
  timeRegistrations: TimeRegistrationReducer
  salaryTypes: List<SalaryType>
  leaveTypes: List<LeaveType>
  projects: List<Project>
  costCenterAccounting: CostCenterAccounting
  costCenters: List<CostCenter>
  departments: List<Department>

  addAlert: addAlertSignature
  createTimeRegistration: (registration: TimeRegistrationCreationFields) => void
  approveTimeRegistrations: (ids: string[]) => void
  unapproveTimeRegistrations: (ids: string[]) => void
  updateTimeRegistration: (registration: TimeRegistrationMutableFields) => void
  deleteTimeRegistration: (id: string) => void
  deleteTimeRegistrationBulk: (
    companyID: string | undefined,
    employeeID: string | undefined,
    type: TimeRegistrationClass
  ) => void
}

// registrations older than this are only loaded on demand, and only when still Pending
const OLD_REGISTRATION_CUTOFF_YEARS = 2
const OLD_REGISTRATION_LOOKBACK_YEARS = 50

export default function DetailedTimeRegistrationTab(props: Props): ReactElement | null {
  const [approving, setApproving] = useState<string[]>([])
  const [deleting, setDeleting] = useState<string[]>([])
  const [modalKey, setModalKey] = useState<number>(1)
  const [editing, setEditing] = useState<boolean>(false)
  const [timeRegID, setTimeRegID] = useState<string | undefined>(undefined)
  const [olderRegistrations, setOlderRegistrations] = useState<TimeRegistration[]>([])
  const [showOlder, setShowOlder] = useState(false)

  const matchesModeClass = (timeRegistration: TimeRegistration): boolean => {
    switch (props.mode) {
      case 'Hours':
        return timeRegistration.class === 'Hours'
      case 'FlexTime':
        return timeRegistration.class === 'Flex'
      case 'Overtime':
        return timeRegistration.class === 'Overtime'
      case 'Project':
        return timeRegistration.class === 'Project Hours'
      default:
        return false
    }
  }

  // registrations right around the cutoff date can be returned by both the current-list fetch and
  // the old-registrations fetch, so anything already loaded is excluded to avoid duplicate rows
  const getLoadedIDs = () => new Set(props.timeRegistrations.timeRegistrations.map((registration) => registration.id))

  useEffect(() => {
    setOlderRegistrations([])
    setShowOlder(false)
    fetchTimeRegistrations(
      undefined,
      props.employee.id,
      undefined,
      formatAPIDate(subYears(getDate(), OLD_REGISTRATION_LOOKBACK_YEARS)),
      formatAPIDate(subYears(getDate(), OLD_REGISTRATION_CUTOFF_YEARS)),
      'Pending'
    ).then((res) => {
      const loadedIDs = getLoadedIDs()
      setOlderRegistrations(
        res.data.filter((registration) => matchesModeClass(registration) && !loadedIDs.has(registration.id))
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.employee.id, props.mode])

  // Registrations still Pending after 2 years are rare stragglers, so they're only pulled in on
  // demand rather than fetched (and re-rendered) on every load of the current, non-settled list.
  // Once one of them is touched (edited/approved/deleted), it's tracked in `timeRegistrations` going
  // forward, so it's excluded here to avoid rendering it twice with stale, locally-fetched data.
  const allTimeRegistrations = showOlder
    ? props.timeRegistrations.timeRegistrations.concat(
        List(olderRegistrations.filter((registration) => !getLoadedIDs().has(registration.id)))
      )
    : props.timeRegistrations.timeRegistrations

  // the edit form looks up its initial values from `timeRegistrations.timeRegistrations` by id, which
  // wouldn't find an old registration since those aren't in the reducer until they're saved once - so
  // it gets the merged list instead, otherwise it would open with a blank form
  const editTimeRegistrations: TimeRegistrationReducer = {
    companyID: props.timeRegistrations.companyID,
    employeeID: props.timeRegistrations.employeeID,
    payRollID: props.timeRegistrations.payRollID,
    fromDate: props.timeRegistrations.fromDate,
    toDate: props.timeRegistrations.toDate,
    loading: props.timeRegistrations.loading,
    loaded: props.timeRegistrations.loaded,
    saving: props.timeRegistrations.saving,
    hashMap: props.timeRegistrations.hashMap,
    error: props.timeRegistrations.error,
    timeRegistrations: allTimeRegistrations,
  }

  const setEditVisibility = useCallback(
    (id: string | boolean) => {
      // Increment modalKey to create a new component
      setModalKey(modalKey + 1)
      if (typeof id === 'boolean') {
        setEditing(id)
        setTimeRegID(undefined)
      } else {
        setEditing(true)
        setTimeRegID(id)
      }
    },
    [setModalKey, modalKey, setEditing, setTimeRegID]
  )

  const saving = props.timeRegistrations.saving
  const wasSaving = usePrevious(saving)
  const savingError = props.timeRegistrations.error

  useEffect(() => {
    if (wasSaving && !saving) {
      if (!savingError) {
        setEditVisibility(false)
        // a deleted old registration never lived in the reducer's list, so it won't disappear on
        // its own - remove it from local state now that the delete has actually succeeded
        if (deleting.length > 0) {
          setOlderRegistrations((prev) => prev.filter((registration) => !deleting.includes(registration.id)))
        }
      }
    }
  }, [wasSaving, saving, savingError, setEditVisibility, deleting])

  const approve = (timeRegistration: TimeRegistration) => {
    setApproving((prev) => [...prev, timeRegistration.id])
    props.approveTimeRegistrations([timeRegistration.id])
  }
  const unapprove = (timeRegistration: TimeRegistration) => {
    setApproving((prev) => prev.filter((id) => id !== timeRegistration.id))
    props.unapproveTimeRegistrations([timeRegistration.id])
  }

  type TimeRegistrationRow = {
    key: string
    id: string
    approved: boolean
    date: DateFormat
    description: string | React.ReactNode
    hours: string
    note: string
    settled: boolean
    original: TimeRegistration
  }

  const getTimeRegistrations = (): TimeRegistrationRow[] => {
    return allTimeRegistrations
      .filter(
        (timeRegistration) =>
          timeRegistration.employeeID === props.employee.id &&
          !timeRegistration.settled &&
          matchesModeClass(timeRegistration)
      )
      .toArray()
      .map((timeRegistration: TimeRegistration): TimeRegistrationRow => {
        let title
        let hours = formatDisplayNumber(timeRegistration.hours)
        if (timeRegistration.class === 'Hours') {
          let rate = null
          if (props.employee.activeContract && props.employee.activeContract.remuneration) {
            const salaryLine = props.employee.activeContract.remuneration.salary.find(
              (row) => row.salaryTypeID === timeRegistration.salaryTypeID
            )
            if (salaryLine) {
              title = salaryLine.title
              rate = salaryLine.rate
            }
          }
          if (!title) {
            title =
              getSalaryType(props.salaryTypes.toArray(), timeRegistration.salaryTypeID)?.title ||
              formatTimeRegistrationClass(timeRegistration.class)
          }
          if (rate) {
            title = t('time_registration_tab.detailed.table.title.rate_format', { title, rate: formatCurrency(rate) })
          }
          if (timeRegistration.projectID) {
            const project = props.projects.find((p) => p.id === timeRegistration.projectID)
            if (project) {
              title = (
                <>
                  {title}
                  <br />
                  <small>{project.name}</small>
                </>
              )
            }
          }
        } else if (timeRegistration.class === 'Flex' || timeRegistration.class === 'Overtime') {
          const hoursCalc = timeRegistration.minutes ? timeRegistration.minutes / 60 : 0
          if (props.mode === 'Overtime') {
            title = formatLeaveTypeName('DenmarkOvertime')
          } else {
            title = formatLeaveTypeName('DenmarkFlexTime')
          }
          if (timeRegistration.minutes && timeRegistration.minutes < 0) {
            hours = t('time_registration_tab.detailed.table.title.earned', {
              hours: formatDisplayNumber(hoursCalc * -1),
            })
          } else {
            hours = t('time_registration_tab.detailed.table.title.used', { hours: formatDisplayNumber(hoursCalc) })
          }
        } else if (timeRegistration.class === 'Project Hours') {
          title =
            props.projects.find((p) => p.id === timeRegistration.projectID)?.name ||
            formatTimeRegistrationClass(timeRegistration.class)
        } else {
          title = formatTimeRegistrationClass(timeRegistration.class)
        }
        return {
          key: timeRegistration.id,
          id: timeRegistration.id,
          approved: timeRegistration.approved,
          date: formatDate(timeRegistration.date),
          description: title,
          hours: hours,
          note: timeRegistration.note,
          settled: timeRegistration.settled,
          original: timeRegistration,
        }
      })
  }

  const approveAll = (e: React.MouseEvent) => {
    e.preventDefault()
    if (window.confirm(t('common.are_you_sure'))) {
      const registrations = getTimeRegistrations()
        .filter((reg) => !reg.approved)
        .map((reg) => reg.original)
      setApproving(registrations.map((reg) => reg.id))
      props.approveTimeRegistrations(registrations.map((reg) => reg.id))
    }
  }

  const remove = (timeRegistration: TimeRegistration) => {
    if (window.confirm(t('common.are_you_sure'))) {
      setDeleting([...deleting, timeRegistration.id])
      props.deleteTimeRegistration(timeRegistration.id)
    }
  }

  const getUnsettledHours = (project: TimeRegistrationClass): string => {
    const hours = allTimeRegistrations
      .filter(
        (timeRegistration) =>
          timeRegistration.employeeID === props.employee.id &&
          timeRegistration.class === project &&
          !timeRegistration.settled
      )
      .reduce((hours, timeRegistration) => {
        if (project === 'Flex' || project === 'Overtime') {
          return hours + (timeRegistration.minutes ? timeRegistration.minutes / 60 : 0)
        } else {
          return hours + (timeRegistration.hours ? timeRegistration.hours : 0)
        }
      }, 0)
    const mul = project === 'Flex' || project === 'Overtime' ? -1 : 1
    return t('unit.hour_count', { count: hours * mul })
  }

  const getColumns = () => {
    return [
      {
        title: t('time_registration_tab.detailed.table.approved'),
        key: 'x1',
        className: 'time-registration-table-approved',
        render: (timeRegistration: TimeRegistrationRow) => {
          if (timeRegistration.approved) {
            return (
              <span className="table-approve-wrapper">
                {t('time_registration_tab.detailed.table.approved.true')}
                {props.canApproveObjects && (
                  <Tooltip title={t('time_registration_tab.detailed.table.approved.remove')}>
                    <span
                      onClick={(e) => {
                        e.preventDefault()
                        unapprove(timeRegistration.original)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Icon type="xSign" />
                    </span>
                  </Tooltip>
                )}
              </span>
            )
          }
          if (approving.indexOf(timeRegistration.id) !== -1) {
            return t('time_registration_tab.detailed.table.saving')
          }
          if (!props.canApproveObjects) {
            return t('time_registration_tab.detailed.table.approved.false')
          }
          return (
            <DumbLink
              onClick={(e) => {
                e.preventDefault()
                approve(timeRegistration.original)
              }}
              type="standard"
            >
              {t('time_registration_tab.detailed.table.approved.approve')}
            </DumbLink>
          )
        },
      },
      { title: t('time_registration_tab.detailed.table.date'), dataIndex: 'date', key: 'date' },
      { title: t('time_registration_tab.detailed.table.description'), dataIndex: 'description', key: 'description' },
      { title: t('time_registration_tab.detailed.table.hours'), dataIndex: 'hours', key: 'hours' },
      {
        title: t('time_registration_tab.detailed.table.note'),
        key: 'x2',
        className: 'employee-table-actions',
        render: (timeRegistration: TimeRegistrationRow) => {
          if (!timeRegistration.note) {
            return null
          }
          return (
            <div>
              <Tooltip title={timeRegistration.note}>
                <span>
                  <Icon type="document" />
                </span>
              </Tooltip>
            </div>
          )
        },
      },
      {
        title: '',
        key: 'x3',
        className: 'employee-table-actions',
        render: (timeRegistration: TimeRegistrationRow) => {
          if (deleting.indexOf(timeRegistration.id) !== -1) {
            return null
          }
          if (timeRegistration.settled) {
            return null
          }
          if (!props.canEditObjects) {
            return null
          }
          return (
            <div>
              <Tooltip title={t('time_registration_tab.detailed.table.actions.edit')}>
                <span onClick={() => setEditVisibility(timeRegistration.id)} style={{ cursor: 'pointer' }}>
                  <Icon type="paperWithPencil" />
                </span>
              </Tooltip>
              <Tooltip title={t('time_registration_tab.detailed.table.actions.delete')}>
                <span
                  onClick={(e) => {
                    e.preventDefault()
                    remove(timeRegistration.original)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Icon type="xSign" />
                </span>
              </Tooltip>
            </div>
          )
        },
      },
    ]
  }

  const hasUnapprovedTimeRegistrations = (): boolean => {
    return getTimeRegistrations().some((reg) => !reg.approved)
  }

  const bulkDelete = () => {
    if (window.confirm(t('time_registration_tab.detailed.confirm.bulk_delete'))) {
      props.deleteTimeRegistrationBulk(undefined, props.employee.id, 'Hours')
    }
  }

  const timeRegistrations = getTimeRegistrations()
  let balanceNote = t('time_registration_tab.detailed.balance_note.hours')
  let currentType: TimeRegistrationClass = 'Hours'
  let addNewLine = t('time_registration_tab.detailed.add_new.hours')
  if (props.mode === 'FlexTime') {
    balanceNote = t('time_registration_tab.detailed.balance_note.flex_time')
    currentType = 'Flex'
    addNewLine = t('time_registration_tab.detailed.add_new.flex_time')
  }
  if (props.mode === 'Overtime') {
    balanceNote = t('time_registration_tab.detailed.balance_note.overtime')
    currentType = 'Overtime'
    addNewLine = t('time_registration_tab.detailed.add_new.overtime')
  }
  if (props.mode === 'Project') {
    balanceNote = t('time_registration_tab.detailed.balance_note.project')
    currentType = 'Project Hours'
    addNewLine = t('time_registration_tab.detailed.add_new.project')
  }
  return (
    <div>
      <p>{balanceNote}</p>
      <Row>
        <Col span={8}>
          <div className="time-registration-stat">
            <strong>{getUnsettledHours(currentType)}</strong>
            <span className="time-registration-hours">{formatTimeRegistrationClass(currentType)}</span>
          </div>
        </Col>
        <Col span={16} className="time-registration-buttons">
          {props.mode === 'Hours' &&
            props.canEditObjects &&
            timeRegistrations.filter((timeReg) => !timeReg.settled).length > 0 && (
              <Button
                onClick={() => bulkDelete()}
                type="primary"
                danger
                style={{ width: '125px', marginRight: '10px' }}
                prefixIcon="xSign"
              >
                {t('time_registration_tab.detailed.delete_bulk')}
              </Button>
            )}

          {props.canEditObjects && (
            <Button
              type="primary"
              onClick={() => setEditVisibility(true)}
              className="register-time-registration"
              prefixIcon="plusCircle"
            >
              {addNewLine}
            </Button>
          )}

          {hasUnapprovedTimeRegistrations() && props.canApproveObjects && (
            <Button type="primary" onClick={approveAll} style={{ marginLeft: 20 }}>
              {t('time_registration_tab.detailed.approve_all')}
            </Button>
          )}
        </Col>
      </Row>

      <Table columns={getColumns()} dataSource={timeRegistrations} pagination={false} />
      {!showOlder && olderRegistrations.length > 0 && (
        <span>
          {t('time_registration_tab.detailed.history.load_older_notice', { years: OLD_REGISTRATION_CUTOFF_YEARS })}{' '}
          &nbsp;
          <Button onClick={() => setShowOlder(true)}>
            {t('time_registration_tab.detailed.history.load_older', { count: olderRegistrations.length })}
          </Button>
        </span>
      )}
      {props.timeRegistrations.saving && (
        <LoadingOverlay
          text={formatSavingText([
            { loading: props.timeRegistrations.saving, text: t('loading.reducer.time_registrations') },
          ])}
        />
      )}

      <Modal
        key={modalKey}
        visible={editing}
        onOk={() => setEditVisibility(false)}
        onCancel={() => setEditVisibility(false)}
        width={376}
        footer={null}
      >
        <DetailedTimeRegistrationEdit
          visible={editing}
          company={props.company}
          employee={props.employee}
          mode={props.mode}
          timeRegistrationID={timeRegID}
          timeRegistrations={editTimeRegistrations}
          salaryTypes={props.salaryTypes}
          leaveTypes={props.leaveTypes}
          projects={props.projects}
          costCenters={props.costCenters}
          departments={props.departments}
          createTimeRegistration={props.createTimeRegistration}
          updateTimeRegistration={props.updateTimeRegistration}
        />
      </Modal>
    </div>
  )
}
